import { getField, updateField } from "vuex-map-fields";
import { VuexModule, Module, Action } from "vuex-module-decorators";
import ResearchDataService from "@/views/sidebar/research-data/research-data.service";
import { 
  createSowsAssessments,
  createDoseTimes,
  SowsAssessment, 
  DoseTime,
  WatchCharging,
  WatchWorn,
  UpsertDosageResponse,
  ParticipantPayment,
  ResearchRewards,
  UpsertPaymentHistory,
  PaymentStatus,
  AppointmentStatus,
  AlertZone,
  BiometricCollectionCount,
  UpsertAppointmentStatus,
  AssessmentSummary
} from "@/views/sidebar/research-data/research-data.types";
import { ResearchParticipant } from "@/views/research/research.types"
import { AssessmentTypes } from "@/views/dashboard/dashboard.types";

@Module({ namespaced: true })
class ResearchData extends VuexModule {
  sowsAssessments: SowsAssessment[] = createSowsAssessments();
  sowsAlertZones: AlertZone[] = [];
  doseTimes: DoseTime[] = createDoseTimes();
  watchWorn = new WatchWorn();
  watchCharging = new WatchCharging();
  biometricRecordCount = new BiometricCollectionCount();
  participantPayment = new ParticipantPayment();
  researchRewards: ResearchRewards[] = [];
  assessmentSummary: AssessmentSummary[] = [];
  paymentStatus = new PaymentStatus();
  appointmentStatus = new AppointmentStatus();
  researchParticipant = new ResearchParticipant();
  pickDate = new Date();

  private _initialState!: Record<string, any>;

  constructor(module: any) {
    super(module);
    this._initialState = { ...this };
  }

  @Action({ rawError: true })
  async fetchSowsAssessments({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const sowsAssessments = await ResearchDataService.GetSowsAssessments(clientId, date, participantId, timezone);
    sowsAssessments.forEach(item => {
      const target = this.sowsAssessments.find(a => a.name === item.name);
      if (target) {
        target.score = item.score;
      }
    });
  }

  @Action({ rawError: true })
  async fetchSowsChartOptions( { clientId }: { clientId: number }) {
    const sowsAlertZones = await ResearchDataService.getAlertZones(AssessmentTypes.SOWS, clientId);
    this.context.commit("updateField", {
      path: "sowsAlertZones",
      value: sowsAlertZones
    });
  }

  @Action({ rawError: true })
  async fetchDoseTimes({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const doseTimes = await ResearchDataService.GetDoseTimes(clientId, date, participantId, timezone);
    doseTimes.forEach(item => {
      const target = this.doseTimes.find(a => a.name === item.name);
      if (target) {
        target.time = item.time;
        target.dosageResponseId = item.dosageResponseId;
        target.doseNumber = item.doseNumber;
      }
    });
  }

  @Action({ rawError: true })
  async fetchAppointmentStatus({ date, clientId, participantId }: { date: string, clientId: number, participantId: number }) {
    const appointmentStatus = await ResearchDataService.GetAppointmentStatus(clientId, date, participantId);
    this.context.commit("updateField", {
      path: "appointmentStatus",
      value: appointmentStatus
    });
  }

  @Action({ rawError: true })
  async fetchWatchWorn({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const watchWorn = await ResearchDataService.GetWatchWorn(clientId, date, participantId, timezone);
    this.watchWorn.am = [...watchWorn.am];
    this.watchWorn.pm = [...watchWorn.pm];
  }

  @Action({ rawError: true })
  async fetchWatchCharging({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const watchStats = await ResearchDataService.GetWatchCharging(clientId, date, participantId, timezone);
    this.watchCharging.am = [...watchStats.am];
    this.watchCharging.pm = [...watchStats.pm];
  }

  @Action({ rawError: true })
  async fetchBiometricRecordCount({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const biometricRecordCount = await ResearchDataService.GetBiometricRecordCount(clientId, date, participantId, timezone);
    this.biometricRecordCount.am = [...biometricRecordCount.am];
    this.biometricRecordCount.pm = [...biometricRecordCount.pm];
  }

  @Action({ rawError: true })
  async upsertDosageResponse(upsertDosageResponse: UpsertDosageResponse) {
    try {
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: null
      }, { root: true });
      await ResearchDataService.UpsertDosageResponse(upsertDosageResponse);
    } catch (e: any) {
      const errorMessage = e.response?.data || e.response.data.message || "An unexpected error occurred.";
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: errorMessage
      }, { root: true });
    }
  }

  @Action({ rawError: true })
  async upsertAppointmentStatus(upsertAppointmentStatus: UpsertAppointmentStatus) {
    try {
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: null
      }, { root: true });
      await ResearchDataService.UpsertAppointmentStatus(upsertAppointmentStatus);
    } catch (e: any) {
      const errorMessage = e.response?.data || e.response.data.message || "An unexpected error occurred.";
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: errorMessage
      }, { root: true });
    }
  }

  @Action({ rawError: true })
  async calculateParticipantPayment({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const participantPayment = await ResearchDataService.CalculateParticipantPayment(clientId, date, participantId, timezone);
    this.context.commit("updateField", {
      path: "participantPayment",
      value: participantPayment
    });
  }

  @Action({ rawError: true })
  async fetchParticipantPaymentSummary({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const researchRewards = await ResearchDataService.GetParticipantPaymentSummary(clientId, date, participantId, timezone);
    this.context.commit("updateField", {
      path: "researchRewards",
      value: researchRewards
    });
  }

  @Action({ rawError: true })
  async upsertPaymentHistory(upsertPaymentHistory: UpsertPaymentHistory) {
    try {
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: null
      }, { root: true });
      await ResearchDataService.UpsertPaymentHistory(upsertPaymentHistory);
    } catch (e: any) {
      const errorMessage = e.response?.data || e.response.data.message || "An unexpected error occurred.";
      this.context.commit("Base/updateField", {
        path: "errorMessage",
        value: errorMessage
      }, { root: true });
    }
  }

  @Action({ rawError: true })
  async fetchParticipantPaymentStatus({ date, clientId, participantId }: { date: string, clientId: number, participantId: number }) {
    const paymentStatus = await ResearchDataService.GetParticipantPaymentStatus(clientId, date, participantId);
    this.context.commit("updateField", {
      path: "paymentStatus",
      value: paymentStatus
    });
  }

  @Action({ rawError: true })
  async fetchAssessmentSummary({ date, clientId, participantId, timezone }: { date: string, clientId: number, participantId: number, timezone: string }) {
    const assessmentSummary = await ResearchDataService.GetAssessmentSummary(clientId, date, participantId, timezone);
    this.context.commit("updateField", {
      path: "assessmentSummary",
      value: assessmentSummary
    });
  }

  @Action({ rawError: true })
  resetState() {
    for (const key of Object.keys(this._initialState)) {
      this.context.commit("updateField", {
        path: key,
        value: this._initialState[key]
      });
    }
  }
}

ResearchData.getters = { getField };
ResearchData.mutations = { updateField };

export default ResearchData;